import { useState } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';

const CATEGORIES = [
  { value: 'bug',     label: '🐛 Bug report' },
  { value: 'feature', label: '💡 Feature request' },
  { value: 'general', label: '💬 General feedback' },
];

/**
 * FeedbackModal — lets any logged-in user send feedback that lands in
 * the `supportRequests` Firestore collection. Viewable at /admin/feedback.
 *
 * Props:
 *   isOpen   boolean
 *   onClose  () => void
 */
export default function FeedbackModal({ isOpen, onClose }) {
  const { user, profile } = useAuth();
  const [category, setCategory] = useState('general');
  const [message,  setMessage]  = useState('');
  const [status,   setStatus]   = useState('idle'); // idle | sending | sent | error

  if (!isOpen) return null;

  const reset = () => {
    setCategory('general');
    setMessage('');
    setStatus('idle');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus('sending');
    try {
      await addDoc(collection(db, 'supportRequests'), {
        uid:         user?.uid   || null,
        email:       user?.email || null,
        displayName: profile?.displayName || null,
        role:        profile?.role        || null,
        category,
        message:     message.trim(),
        url:         window.location.href,
        userAgent:   navigator.userAgent,
        createdAt:   new Date().toISOString(),
        status:      'new',
      });
      setStatus('sent');
      setTimeout(() => {
        handleClose();
      }, 1800);
    } catch (err) {
      Sentry.captureException(err);
      setStatus('error');
    }
  };

  return (
    <>
      <div className="feedback-backdrop" onClick={handleClose} aria-hidden="true" />
      <div
        className="feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Send feedback"
      >
        <div className="feedback-header">
          <h2 className="feedback-title">Send feedback</h2>
          <button className="feedback-close" onClick={handleClose} aria-label="Close">✕</button>
        </div>

        {status === 'sent' ? (
          <div className="feedback-success">
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
            <p style={{ margin: 0, fontWeight: 600 }}>Thanks — we'll take a look!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-form">
            {/* Category picker */}
            <div className="feedback-categories">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`feedback-cat-btn${category === c.value ? ' feedback-cat-btn--active' : ''}`}
                  onClick={() => setCategory(c.value)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <textarea
              className="feedback-textarea"
              placeholder="Tell us what's on your mind…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              required
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />

            {status === 'error' && (
              <p className="feedback-error">Something went wrong — please try again.</p>
            )}

            <div className="feedback-actions">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={!message.trim() || status === 'sending'}
              >
                {status === 'sending' ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
